import dayGridPlugin from '@fullcalendar/daygrid';
import FullCalendar from '@fullcalendar/react';
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Card } from "@mui/material";
import { getInspectionReports, getInspectionReportSummary } from "api/inspection";
import colors from 'assets/theme/base/colors';
import CustomReactSelect from "components/CustomReactSelect";
import BaseTable from "components/DataTable/BaseTable";
import MDBox from "components/MDBox";
import MDInput from 'components/MDInput';
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import useFilterStore from 'store/useFilterStore';
import { formatDateTime, getYearMonthString } from "utils/helper";

const TABLE_COLUMNS = [
    { id: "report_id", Header: "Report Id", accessor: "report_id", align: "left", sortable: true },
    { id: "category", Header: "Category", accessor: "category", align: "left" },
    { id: "ride", Header: "Ride", accessor: "ride", align: "left" },
    { id: "type", Header: "Type", accessor: "type", align: "left" },
    { id: "created_at", Header: "Created At", accessor: "created_at", align: "left", sortable: true },
    { id: "user", Header: "Issued By", accessor: "user", align: "left", sortable: true },
];

export const parentRoute = '/inspections-reports';

function InspectionsReportTable() {
    const navigate = useNavigate();
    const location = useLocation();
    const { getFilters, setFilters } = useFilterStore();
    const calendarRef = useRef(null)
    const [refreshKey, setRefreshKey] = useState(0);
    const savedState = location.state || { sort: "created_at", dir: "desc" };
    const [params, setParams] = useState({});
    const [currentMonthYear, setCurrentMonthYear] = useState(getYearMonthString(new Date()));
    const [calendarEvents, setCalendarEvents] = useState([]);
    const [showCalendar, setShowCalendar] = useState(true);
    const calenadarWrapperRef = useRef();
    const currentPath = window.location.pathname;

    useEffect(() => {
        const record = getFilters(currentPath);
        if (record?.customParams) {
            setParams(record.customParams);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Persist custom filters whenever they change (merge with existing baseTable state)
    useEffect(() => {
        const record = getFilters(currentPath);
        setFilters(currentPath, {
            baseTable: record.baseTable, // preserve the table paging/sort managed by BaseTable
            customParams: params
        });
    }, [params, currentPath]);


    useEffect(() => {
        getInspectionReportSummary(currentMonthYear).then((res) => {
            const events = res?.data?.map(report => ({
                title: report.count + " reports",
                date: report.date,
                display: 'block',
                backgroundColor: '#9974C1',
                borderColor: colors.primary.dark,
                textColor: '#fff',
            }));

            setCalendarEvents(events);
        });
    }, [currentMonthYear]);


    const fetchWithFilters = useCallback(async (filters = {}) => {
        const filterParams = Object.fromEntries(
            Object.entries({ ...filters, ...params }).filter(([key, value]) => value != null && value !== '')
        );

        const response = await getInspectionReports(filterParams);
        return response
    }, [params]);


    const createTableRow = useCallback((item) => ({
        report_id: <MDTypography component="div" variant="standard" color="text">
            {item.report_id ?? "-"}
            {item.late || item.inspection_checklist_skip_item?.length > 0 ? <span style={{ color: 'red', fontSize: 16 }}>{item.late ? " *" : " ≫"}</span> : null}
        </MDTypography>,
        category: <MDTypography component="div" variant="standard" color="text">{item.category?.name ?? "-"}</MDTypography>,
        ride: <MDTypography component="div" variant="standard" color="text">{item.ride?.name ?? "-"}</MDTypography>,
        type: <MDTypography component="div" variant="standard" color="text">{item.type?.name ?? "-"}</MDTypography>,
        created_at: <MDTypography component="div" variant="standard" color="text">
            {formatDateTime(item.created_at)}
        </MDTypography>,
        user: <MDTypography component="div" variant="standard" color="text">{item.user?.full_name ?? "-"}</MDTypography>,
    }), []);

    const filtersUI = (
        <>
            <MDBox sx={{ flex: 1, minWidth: 130 }}>
                <CustomReactSelect
                    label="Has Error"
                    name="hasError"
                    value={params.hasError !== undefined ? { value: params.hasError, label: params.hasError ? 'Yes' : 'No' } : ""}
                    onChange={option => setParams(prev => ({ ...prev, hasError: option?.value }))}
                    options={[
                        { value: 1, label: 'Yes' },
                        { value: 0, label: 'No' }
                    ]}
                    isClearable
                />
            </MDBox>
            <MDBox sx={{ flex: 1, minWidth: 180 }}>
                <MDInput
                    fullWidth
                    label="Date"
                    name="date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={params?.date || ''}
                    onChange={e => setParams(prev => ({ ...prev, date: e.target.value }))}
                />
            </MDBox>
        </>
    );

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <Card ref={calenadarWrapperRef} sx={{ background: '#fff', p: 3, mb: 3 }}>
                {/* <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <MDTypography variant="h6" fontWeight="medium">
                        Daily Inspection Reports
                    </MDTypography>
                    <MDBox
                        component="button"
                        onClick={() => setShowCalendar(!showCalendar)}
                        sx={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'primary.main',
                            '&:hover': { textDecoration: 'underline' }
                        }}
                    >
                        <MDTypography variant="button">
                            {showCalendar ? 'Hide Calendar' : 'Show Calendar'}
                        </MDTypography>
                    </MDBox>
                </MDBox> */}

                {showCalendar && (
                    <FullCalendar
                        ref={calendarRef}
                        plugins={[dayGridPlugin]}
                        initialView="dayGridMonth"
                        events={calendarEvents}
                        height="auto"
                        eventDisplay="block"
                        dayMaxEvents={false}
                        headerToolbar={{
                            left: 'title',
                            right: 'today customPrev customNext'
                        }}
                        customButtons={{
                            today: {
                                text: 'Today', click: function () {
                                    const calendarApi = calendarRef.current.getApi()
                                    calendarApi.today();
                                    const currentDate = calendarApi.getDate();
                                    setCurrentMonthYear(getYearMonthString(currentDate));
                                }
                            },
                            customPrev: {
                                icon: 'chevron-left',
                                click: function () {
                                    const calendarApi = calendarRef.current.getApi()
                                    calendarApi.prev();
                                    const currentDate = calendarApi.getDate();
                                    setCurrentMonthYear(getYearMonthString(currentDate));
                                }
                            },
                            customNext: {
                                icon: 'chevron-right',
                                click: function () {
                                    const calendarApi = calendarRef.current.getApi()
                                    calendarApi.next();
                                    const currentDate = calendarApi.getDate();
                                    setCurrentMonthYear(getYearMonthString(currentDate));
                                }
                            }
                        }}
                        eventClick={(info) => {
                            const clickedDate = info.event.startStr;
                            // Filter table by clicked date and refresh table
                            setParams({ date: clickedDate });
                            // Scroll to bottom after state update
                            setTimeout(() => {
                                window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                            }, 100);
                        }}
                        eventDidMount={(info) => {
                            if (info.event.extendedProps.count) {
                                info.el.title = `${info.event.extendedProps.count} inspection report${info.event.extendedProps.count > 1 ? 's' : ''} on ${info.event.startStr}`;
                            }
                        }}
                    />
                )}
            </Card>

            <BaseTable
                key={refreshKey}
                title="Inspections Reports"
                columns={TABLE_COLUMNS}
                fetchData={fetchWithFilters}
                defaultState={savedState}
                createTableRow={createTableRow}
                onAddClick={() => navigate(`${parentRoute}/add-inspection-report`, { state: savedState })}
                showSearch
                searchPlaceholder="Search by report Id"
                additionalFilters={filtersUI}
                showFrequencyTypeFilter
                showInspectionCategoryFilter
                onClickRow={(row, key) => { navigate(`${parentRoute}/${row.id}`, { state: savedState }) }}
            />
        </DashboardLayout>
    );
}

export default InspectionsReportTable;